import { createContext, useContext, useMemo, ReactNode } from 'react';
import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { getPlanTier } from 'utils/planUtils';


export type BillingFlowType = 'free' | 'plg' | 'slg';

interface BillingFlowContextValue {
  flowType: BillingFlowType;
}

const BillingFlowContext = createContext<BillingFlowContextValue | null>(null);

const FREE_TIER_SLUGS = new Set<string>([
  'free',
  // TODO there is no 'free-trial' slug
  'free-trial', // defensive guard — not a real slug, it's `free` + the isFreeTrial flag
  'standard_extended_trial', // auto-applied free extension (distinct from `extended_trial`, see SLG_SLUGS)
  // affiliate* slugs are sold as "Mini" plans in Chargebee — legacy naming kept.
  // TODO there's no 'mini' slug
  'mini',
  'affiliate',
  'annual_affiliate',
  'yearly_affiliate',
]);

// Matched via getPlanTier so vintages (hobby_1, hobby_2, yearly_hobby_2, etc.) all resolve.
const PLG_TIERS = new Set<string>(['hobby', 'startup', 'business', 'scaling', 'professional', 'advanced']);

// student_npo needs PLG flow to keep LimitHandlingCard visible (gated on flowType !== 'free').
const PLG_SLUGS = new Set<string>([
  'student_npo',
]);

// `extended_trial` here is the sales-managed trial (distinct from `standard_extended_trial`, see FREE_TIER_SLUGS).
const SLG_SLUGS = new Set<string>(['custom', 'extended_trial']);
const SLG_PREFIXES = ['enterprise'];

function determineFlowType(planSlug: string | undefined): BillingFlowType {
  if (!planSlug || FREE_TIER_SLUGS.has(planSlug)) return 'free';
  if (SLG_SLUGS.has(planSlug) || SLG_PREFIXES.some(p => planSlug.startsWith(p))) return 'slg';
  if (PLG_SLUGS.has(planSlug) || PLG_TIERS.has(getPlanTier(planSlug))) return 'plg';
  return 'slg'; // unknown → contact-sales by default
}

export function BillingFlowProvider({ children }: { children: ReactNode }) {
  const { activeSubscription } = useActiveSubscription();

  const flowType = useMemo(() => {
    const planSlug = activeSubscription?.planSlug;
    return determineFlowType(planSlug);
  }, [ activeSubscription?.planSlug ]);

  return (
    <BillingFlowContext.Provider value={{ flowType }}>
      {children}
    </BillingFlowContext.Provider>
  );
}

export function useBillingFlow(): BillingFlowContextValue {
  const context = useContext(BillingFlowContext);
  if (!context) {
    // TODO this hook should be used even without a Provider - it does not load/fetch anything other than the active
    //  subscription and only makes decisions based on the plan slug
    throw new Error('useBillingFlow must be used within BillingFlowProvider');
  }
  return context;
}
